// Pure, React-free helpers for the admin dashboard: normalizing timecard fields
// from mixed API casings, building the review roster + status tallies, and
// formatting names/dates/hospital summaries. Extracted from AdminDashboard so
// they can be unit-tested in isolation.

export function getTimecardId(timecard) {
  return Number(timecard?.timecardId ?? timecard?.TimecardId ?? 0);
}

export function getTimecardContractorId(timecard) {
  return Number(timecard?.contractorId ?? timecard?.ContractorId ?? 0);
}

export function getTimecardStatus(timecard) {
  return String(timecard?.status ?? timecard?.Status ?? "draft").toLowerCase();
}

export function formatStatusLabel(status) {
  return status === "none" ? "not started" : status;
}

export function parseLocalDate(value) {
  if (!value) {
    return null;
  }

  const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const parsed = new Date(
    Number.parseInt(match[1], 10),
    Number.parseInt(match[2], 10) - 1,
    Number.parseInt(match[3], 10)
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatContractorName(firstName, lastName, emailAddress, loginUsername) {
  const displayName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  return displayName || emailAddress || loginUsername || "Unknown contractor";
}

export function sortContractors(left, right) {
  const lastNameComparison = (left.lastName ?? "").localeCompare(right.lastName ?? "");
  if (lastNameComparison !== 0) {
    return lastNameComparison;
  }

  return (left.firstName ?? "").localeCompare(right.firstName ?? "");
}

export function formatLastLogin(value) {
  if (!value) {
    return "Never";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "Unknown";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(parsed);
}

export function buildReviewItems(contractors, timecards) {
  const timecardsByContractorId = new Map(
    timecards.map((timecard) => [getTimecardContractorId(timecard), timecard])
  );

  // Build a row for every active contractor. Contractors without a timecard for
  // the selected week show as "none" (not started) so an admin can create one.
  return contractors
    .filter((contractor) => contractor.isActive ?? true)
    .map((contractor) => {
      const contractorId = Number(contractor.contractorId);
      const timecard = timecardsByContractorId.get(contractorId) ?? null;

      return {
        contractorId,
        contractorName: formatContractorName(
          contractor?.firstName,
          contractor?.lastName,
          contractor?.emailAddress,
          contractor?.loginUsername
        ),
        firstName: contractor?.firstName ?? "",
        lastName: contractor?.lastName ?? "",
        emailAddress: contractor?.emailAddress ?? "",
        loginUsername: contractor?.loginUsername ?? "",
        isActive: contractor?.isActive ?? true,
        status: timecard ? getTimecardStatus(timecard) : "none",
        timecard,
        timecardId: timecard ? getTimecardId(timecard) : 0,
        updatedUtc: timecard?.updatedUtc ?? null,
        submittedUtc: timecard?.submittedUtc ?? null,
        emailedUtc: timecard?.emailedUtc ?? null,
        primaryHospitalName: timecard?.primaryHospitalName ?? null,
        hospitalCount: timecard?.hospitalCount ?? 0,
        hospitalNames: timecard?.hospitalNames ?? []
      };
    })
    .sort(sortContractors);
}

export function buildStatusCounts(reviewItems) {
  return reviewItems.reduce(
    (counts, reviewItem) => {
      counts.all += 1;
      counts[reviewItem.status] = (counts[reviewItem.status] ?? 0) + 1;
      return counts;
    },
    {
      all: 0,
      approved: 0,
      submitted: 0,
      draft: 0,
      none: 0
    }
  );
}

export function formatRelativeTime(utcValue) {
  if (!utcValue) return null;
  const date = new Date(utcValue);
  if (Number.isNaN(date.getTime())) return null;

  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (localDate >= today) return `${diffHours}h ago`;
  if (localDate >= yesterday) return "Yesterday";

  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays < 7) return date.toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" });
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function formatHospitalSummary(primaryHospitalName, hospitalCount) {
  if (!hospitalCount) return null;
  if (hospitalCount === 1) return primaryHospitalName || null;
  if (hospitalCount === 2) return primaryHospitalName ? `${primaryHospitalName} +1` : "2 hospitals";
  return `${hospitalCount} hospitals`;
}

export function sortDepartmentNames(departmentNames) {
  return [...departmentNames].sort((left, right) => left.localeCompare(right));
}

export function appendDepartmentName(departmentNames, nextDepartmentName) {
  const trimmedName = nextDepartmentName.trim();
  if (!trimmedName) {
    return departmentNames;
  }

  if (departmentNames.some((departmentName) => departmentName.toLowerCase() === trimmedName.toLowerCase())) {
    return departmentNames;
  }

  return sortDepartmentNames([...departmentNames, trimmedName]);
}
